import { Component } from '@theme/component';

/**
 * A custom element that manages accessible keyboard navigation for the featured
 * content carousel inside the header drawer. It uses IntersectionObserver to
 * track which slides are visible, hides off-screen slides from assistive
 * technology, and wires up previous / next navigation buttons.
 *
 * @extends {Component}
 */
class MenuDrawerCarousel extends Component {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {Set<Element>} */
  #visibleSlides = new Set();

  connectedCallback() {
    super.connectedCallback();
    this.#init();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
  }

  /** @returns {Element[]} All slide elements inside this carousel */
  get #slides() {
    return [...this.querySelectorAll('[role="group"][aria-roledescription="slide"]')];
  }

  /** @returns {Element | null} The scrollable list container */
  get #list() {
    return this.querySelector('.menu-drawer__featured-content-list');
  }

  /** @returns {HTMLButtonElement | null} */
  get #prevBtn() {
    return this.querySelector('.menu-drawer__carousel-prev');
  }

  /** @returns {HTMLButtonElement | null} */
  get #nextBtn() {
    return this.querySelector('.menu-drawer__carousel-next');
  }

  #init() {
    const slides = this.#slides;
    if (!slides.length) return;

    this.#setupIntersectionObserver(slides);
    this.#setupNavigation(slides);
  }

  /**
   * Observe each slide with IntersectionObserver relative to the scrollable
   * list, updating aria-hidden and tabindex as slides enter or leave the
   * visible area.
   *
   * @param {Element[]} slides
   */
  #setupIntersectionObserver(slides) {
    const list = this.#list;
    if (!list) return;

    this.#observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.#visibleSlides.add(entry.target);
            this.#setSlideVisibility(entry.target, true);
          } else {
            this.#visibleSlides.delete(entry.target);
            this.#setSlideVisibility(entry.target, false);
          }
        });
        this.#updateButtonStates(slides);
      },
      { root: list, threshold: 0.5 }
    );

    slides.forEach((slide) => this.#observer?.observe(slide));
  }

  /**
   * Show or hide a slide for assistive technology.
   * When hidden, saves and removes the tabindex from all focusable children.
   *
   * @param {Element} slide
   * @param {boolean} visible
   */
  #setSlideVisibility(slide, visible) {
    if (visible) {
      slide.removeAttribute('aria-hidden');
      slide.querySelectorAll('a, button').forEach((el) => {
        const saved = el.getAttribute('data-carousel-tabindex');
        if (saved !== null) {
          if (saved === '') {
            el.removeAttribute('tabindex');
          } else {
            el.setAttribute('tabindex', saved);
          }
          el.removeAttribute('data-carousel-tabindex');
        }
      });
    } else {
      slide.setAttribute('aria-hidden', 'true');
      slide.querySelectorAll('a, button').forEach((el) => {
        const current = el.getAttribute('tabindex') ?? '';
        el.setAttribute('data-carousel-tabindex', current);
        el.setAttribute('tabindex', '-1');
      });
    }
  }

  /**
   * Wire up click handlers on the prev and next buttons.
   *
   * @param {Element[]} slides
   */
  #setupNavigation(slides) {
    this.#prevBtn?.addEventListener('click', () => this.#navigate('prev', slides));
    this.#nextBtn?.addEventListener('click', () => this.#navigate('next', slides));
  }

  /**
   * Scroll the carousel to the slide adjacent to the current visible range.
   *
   * @param {'prev' | 'next'} direction
   * @param {Element[]} slides
   */
  #navigate(direction, slides) {
    const list = this.#list;
    if (!list) return;

    const visibleArray = slides.filter((s) => this.#visibleSlides.has(s));
    if (!visibleArray.length) return;

    let targetSlide;
    if (direction === 'prev') {
      const firstVisible = visibleArray[0];
      const currentIndex = firstVisible ? slides.indexOf(firstVisible) : 0;
      targetSlide = slides[Math.max(0, currentIndex - 1)];
    } else {
      const lastVisible = visibleArray[visibleArray.length - 1];
      const currentIndex = lastVisible ? slides.indexOf(lastVisible) : 0;
      targetSlide = slides[Math.min(slides.length - 1, currentIndex + 1)];
    }

    targetSlide?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
  }

  /**
   * Disable a button when its boundary slide is already in the visible set.
   *
   * @param {Element[]} slides
   */
  #updateButtonStates(slides) {
    const prevBtn = this.#prevBtn;
    const nextBtn = this.#nextBtn;
    if (!prevBtn || !nextBtn) return;

    const firstSlide = slides[0];
    const lastSlide = slides[slides.length - 1];
    prevBtn.disabled = firstSlide !== undefined && this.#visibleSlides.has(firstSlide);
    nextBtn.disabled = lastSlide !== undefined && this.#visibleSlides.has(lastSlide);
  }
}

if (!customElements.get('menu-drawer-carousel')) {
  customElements.define('menu-drawer-carousel', MenuDrawerCarousel);
}
